import React, { useState, useRef } from 'react';
import { Textarea } from './ui/textarea';

function MentionTextarea({ 
  value, 
  onChange, 
  employees = [], 
  onMentionsChange,
  placeholder = "Add a comment...",
  className = "",
  rows = 3,
  ...props 
}) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const inputRef = useRef(null);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    onChange(e);
    
    // Find @ symbol before cursor
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      
      // Check if there's a space after @ (which would close the mention)
      if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
        setShowSuggestions(false);
        return;
      }
      
      // Filter employees based on text after @
      const filtered = employees.filter(emp =>
        emp.name.toLowerCase().includes(textAfterAt.toLowerCase())
      );
      
      if (filtered.length > 0 && textAfterAt.length >= 0) {
        setSuggestions(filtered);
        setShowSuggestions(true);
        setMentionStart(lastAtIndex);
        setSelectedIndex(0);
      } else {
        setShowSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
    }
    
    // Extract all mentions from the text
    extractMentions(newValue);
  };

  const extractMentions = (text) => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const foundMentions = [];
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      foundMentions.push(match[2]); // user_id
    }
    
    if (onMentionsChange) {
      onMentionsChange(foundMentions);
    }
  };

  const insertMention = (employee) => {
    const cursorPos = inputRef.current.selectionStart;
    const textBeforeMention = value.substring(0, mentionStart);
    const textAfterCursor = value.substring(cursorPos);
    
    // Format: @[Name](user_id)
    const mentionText = `@[${employee.name}](${employee.user_id})`;
    const newValue = textBeforeMention + mentionText + ' ' + textAfterCursor;
    
    // Update value
    const syntheticEvent = {
      target: {
        value: newValue,
        selectionStart: mentionStart + mentionText.length + 1
      }
    };
    onChange(syntheticEvent);
    
    // Close suggestions
    setShowSuggestions(false);
    setMentionStart(-1);
    
    // Extract mentions
    extractMentions(newValue);
    
    // Focus back on input
    setTimeout(() => {
      inputRef.current?.focus();
      const newCursorPos = mentionStart + mentionText.length + 1;
      inputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    // Only handle suggestion navigation, NOT form submission
    if (!showSuggestions) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' && showSuggestions) {
      e.preventDefault();
      insertMention(suggestions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Display text with mentions highlighted
  const displayValue = value.replace(
    /@\[([^\]]+)\]\(([^)]+)\)/g,
    '@$1'
  );

  return (
    <div className="relative">
      <Textarea
        ref={inputRef}
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`min-h-[80px] resize-none ${className}`}
        rows={rows}
        {...props}
      />
      
      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          className="absolute z-50 w-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
          data-testid="mention-suggestions-dropdown"
        >
          {suggestions.map((employee, index) => (
            <button
              key={employee.user_id}
              type="button"
              onClick={() => insertMention(employee)}
              className={`w-full text-left px-4 py-2 hover:bg-stone-100 transition-colors ${
                index === selectedIndex ? 'bg-primary/10' : ''
              }`}
              data-testid={`mention-suggestion-${employee.user_id}`}
            >
              <div className="font-medium text-stone-800">{employee.name}</div>
              <div className="text-xs text-stone-500">{employee.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default MentionTextarea;
